import { useEffect, useRef, useState } from 'react';

const Hero3DScene = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [time, setTime] = useState(0);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width - 0.5) * 15;
      const y = ((e.clientY - rect.top) / rect.height - 0.5) * 15;
      setMousePos({ x, y });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setTime(t => t + 0.02);
    }, 16);
    return () => clearInterval(interval);
  }, []);

  return (
    <div 
      ref={containerRef}
      className="relative w-full h-[400px] md:h-[500px] perspective-[2000px] hidden md:block overflow-hidden"
    >
      {/* Ambient Light Rays */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(6)].map((_, i) => (
          <div
            key={`ray-${i}`}
            className="absolute h-[200%] w-1 bg-gradient-to-b from-transparent via-primary/10 to-transparent blur-sm"
            style={{
              left: `${15 + i * 15}%`,
              top: '-50%',
              transform: `rotate(${20 + i * 5}deg)`,
              opacity: 0.3 + Math.sin(time + i) * 0.2,
              animation: `rayPulse ${3 + i * 0.5}s ease-in-out infinite`,
            }}
          />
        ))}
      </div>

      {/* Central 3D Card */}
      <div 
        className="absolute left-1/2 top-1/2 w-72 h-96 transition-transform duration-500 ease-out"
        style={{
          transform: `translate(-50%, -50%) rotateY(${mousePos.x}deg) rotateX(${-mousePos.y}deg) translateZ(0)`,
          transformStyle: 'preserve-3d',
        }}
      >
        {/* Main Card with Glass Effect */}
        <div 
          className="absolute inset-0 rounded-3xl overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.9) 0%, rgba(236, 72, 153, 0.8) 50%, rgba(59, 130, 246, 0.9) 100%)',
            boxShadow: `
              0 25px 50px -12px rgba(139, 92, 246, 0.4),
              0 0 0 1px rgba(255, 255, 255, 0.1) inset,
              0 -20px 40px rgba(255, 255, 255, 0.1) inset
            `,
            backdropFilter: 'blur(20px)',
            transform: 'translateZ(0)',
          }}
        >
          {/* Holographic Shimmer */}
          <div 
            className="absolute inset-0 opacity-30"
            style={{
              background: `linear-gradient(${45 + time * 20}deg, transparent 30%, rgba(255,255,255,0.4) 50%, transparent 70%)`,
              backgroundSize: '200% 200%',
            }}
          />
          
          {/* Glass Reflection */}
          <div className="absolute inset-0 bg-gradient-to-b from-white/30 via-transparent to-black/20" />
          
          {/* Content */}
          <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-white">
            {/* 3D Icon Container */}
            <div 
              className="w-24 h-24 rounded-2xl flex items-center justify-center mb-6 relative"
              style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.1) 100%)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.2), 0 0 0 1px rgba(255,255,255,0.2) inset',
                transform: `translateZ(40px) rotateY(${Math.sin(time) * 5}deg)`,
                transformStyle: 'preserve-3d',
              }}
            >
              <svg className="w-12 h-12 drop-shadow-lg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M12 2L2 7l10 5 10-5-10-5z" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M2 17l10 5 10-5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M2 12l10 5 10-5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              
              {/* Icon Glow */}
              <div className="absolute inset-0 rounded-2xl bg-white/20 blur-xl" />
            </div>
            
            <h3 
              className="text-2xl font-bold mb-3 tracking-tight"
              style={{ 
                textShadow: '0 2px 10px rgba(0,0,0,0.3)',
                transform: 'translateZ(30px)',
              }}
            >
              AI Studio
            </h3>
            <p 
              className="text-sm text-white/80 text-center leading-relaxed"
              style={{ transform: 'translateZ(20px)' }}
            >
              Create stunning content with next-gen AI
            </p>
            
            {/* Status Indicators */}
            <div 
              className="flex gap-2 mt-6"
              style={{ transform: 'translateZ(25px)' }}
            >
              {['Active', 'Pro', 'AI'].map((label, i) => (
                <span 
                  key={label}
                  className="px-3 py-1 text-xs font-medium rounded-full"
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    animationDelay: `${i * 0.1}s`,
                  }}
                >
                  {label}
                </span>
              ))}
            </div>
          </div>
          
          {/* Animated Particles */}
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full bg-white/60"
              style={{
                width: 3 + Math.random() * 4,
                height: 3 + Math.random() * 4,
                left: `${10 + i * 12}%`,
                top: `${20 + Math.sin(time * 2 + i) * 30}%`,
                filter: 'blur(0.5px)',
                opacity: 0.5 + Math.sin(time * 3 + i) * 0.3,
                transform: `translateZ(${20 + i * 5}px)`,
              }}
            />
          ))}
        </div>

        {/* Floating Orbs */}
        <div 
          className="absolute -left-20 top-8 w-16 h-16 rounded-full"
          style={{ 
            background: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)',
            boxShadow: '0 10px 40px rgba(6, 182, 212, 0.5), 0 0 20px rgba(6, 182, 212, 0.3) inset',
            transform: `translateZ(80px) translateY(${Math.sin(time * 1.5) * 15}px)`,
            animation: 'float 4s ease-in-out infinite',
          }}
        >
          <div className="absolute inset-2 rounded-full bg-gradient-to-br from-white/40 to-transparent" />
        </div>
        
        <div 
          className="absolute -right-16 top-24 w-12 h-12 rounded-full"
          style={{ 
            background: 'linear-gradient(135deg, #ec4899 0%, #a855f7 100%)',
            boxShadow: '0 10px 40px rgba(236, 72, 153, 0.5), 0 0 15px rgba(236, 72, 153, 0.3) inset',
            transform: `translateZ(60px) translateY(${Math.sin(time * 1.8 + 1) * 12}px)`,
            animation: 'float 3.5s ease-in-out infinite 0.5s',
          }}
        >
          <div className="absolute inset-1.5 rounded-full bg-gradient-to-br from-white/40 to-transparent" />
        </div>
        
        <div 
          className="absolute -right-10 bottom-20 w-10 h-10 rounded-xl rotate-45"
          style={{ 
            background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
            boxShadow: '0 10px 40px rgba(245, 158, 11, 0.5)',
            transform: `translateZ(100px) translateY(${Math.sin(time * 2 + 2) * 10}px) rotate(${45 + time * 10}deg)`,
          }}
        >
          <div className="absolute inset-1 rounded-lg bg-gradient-to-br from-white/30 to-transparent" />
        </div>
        
        <div 
          className="absolute -left-14 bottom-16 w-8 h-8 rounded-full"
          style={{ 
            background: 'linear-gradient(135deg, #10b981 0%, #06b6d4 100%)',
            boxShadow: '0 8px 30px rgba(16, 185, 129, 0.5)',
            transform: `translateZ(70px) translateY(${Math.sin(time * 2.2 + 3) * 8}px)`,
            animation: 'float 3s ease-in-out infinite 1s',
          }}
        >
          <div className="absolute inset-1 rounded-full bg-gradient-to-br from-white/40 to-transparent" />
        </div>

        {/* Ring Element */}
        <div 
          className="absolute -left-8 top-1/2 w-20 h-20 rounded-full border-4 border-purple-400/50"
          style={{ 
            transform: `translateZ(50px) translateY(-50%) rotateX(${70 + mousePos.y}deg) rotateZ(${time * 30}deg)`,
            boxShadow: '0 0 20px rgba(168, 85, 247, 0.3)',
          }}
        />
      </div>

      {/* Background Glow Effects */}
      <div 
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(139, 92, 246, 0.25) 0%, transparent 70%)',
          filter: 'blur(40px)',
          opacity: 0.8 + Math.sin(time) * 0.2,
        }}
      />
      <div 
        className="absolute left-1/4 top-1/3 w-80 h-80 rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(59, 130, 246, 0.2) 0%, transparent 70%)',
          filter: 'blur(50px)',
          opacity: 0.6 + Math.sin(time + 1) * 0.2,
        }}
      />
      <div 
        className="absolute right-1/4 bottom-1/3 w-64 h-64 rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(236, 72, 153, 0.2) 0%, transparent 70%)',
          filter: 'blur(50px)',
          opacity: 0.6 + Math.sin(time + 2) * 0.2,
        }}
      />

      {/* Grid Floor Effect */}
      <div 
        className="absolute bottom-0 left-0 right-0 h-40 overflow-hidden opacity-20"
        style={{
          perspective: '500px',
          perspectiveOrigin: '50% 0%',
        }}
      >
        <div 
          className="absolute inset-0"
          style={{
            background: `
              linear-gradient(90deg, rgba(139, 92, 246, 0.3) 1px, transparent 1px),
              linear-gradient(rgba(139, 92, 246, 0.3) 1px, transparent 1px)
            `,
            backgroundSize: '40px 40px',
            transform: 'rotateX(60deg) translateY(-50%)',
            transformOrigin: 'center bottom',
          }}
        />
      </div>
    </div>
  );
};

export default Hero3DScene;
